/**
 * User data hooks using TanStack Query
 *
 * Replaces the Redux userSlice with:
 * - Automatic caching (5 minute stale time)
 * - Deduplication of requests
 * - Background refetching
 */

import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api.service';
import { getCurrentUser } from '../../services/api/users.api';
import { queryKeys } from '../../lib/queryClient';
import type { UserDoc } from '@bower/shared';

export interface User {
  id: string;
  name: string | null;
  email: string;
  displayName: string;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  onboarded?: boolean;
  /** null means the checklist was never initialised (or was cleared); non-null means it exists and may need fetching. */
  onboardingChecklist: unknown | null;
  /**
   * True when the verified Firebase JWT carries an `internalAdmin: true`
   * custom claim. Read in the /admin route gate; mirrors the backend
   * `requireInternalAdmin` check so non-admins are redirected before
   * the page even renders.
   */
  internalAdmin?: boolean;
  /**
   * The unified setup list's flag as the SERVER evaluated it for this user. The
   * fetch gate reads it beside the client flag, which a build with no PostHog
   * key (staging) never evaluates.
   */
  setupListEnabled?: boolean;
  /**
   * Personal research role (onboarding profile), e.g. 'principal_investigator'.
   * PERSONAL — this user's own; drives their suggested-prompt personalisation.
   * Editable in Account settings. Enum key from RESEARCH_ROLE_OPTIONS.
   */
  researchRole?: string | null;
  researchRoleOther?: string | null;
  /** Public 16-digit ORCID iD when saved — implies publications may be imported. */
  orcidId?: string | null;
}

export type CurrentUserApiData = UserDoc & {
  lastLoginAt?: string;
  onboarded?: boolean;
  onboardingChecklist?: unknown | null;
  internalAdmin?: boolean;
  setupListEnabled?: boolean;
  settings?: UserDoc['settings'] & {
    researchProfile?: { role?: string | null; roleOther?: string | null };
  };
};

export function normalizeCurrentUser(userData: CurrentUserApiData): User {
  return {
    id: userData.id,
    name: userData.name,
    email: userData.email,
    displayName: userData.name || userData.email.split('@')[0],
    lastLoginAt: userData.lastLoginAt,
    createdAt: userData.createdAt,
    updatedAt: userData.updatedAt,
    onboarded: userData.onboarded === true,
    onboardingChecklist: userData.onboardingChecklist ?? null,
    internalAdmin: userData.internalAdmin === true,
    setupListEnabled: userData.setupListEnabled === true,
    researchRole: userData.settings?.researchProfile?.role ?? null,
    researchRoleOther: userData.settings?.researchProfile?.roleOther ?? null,
    orcidId: userData.orcidId ?? null,
  };
}

/**
 * Fetch the current authenticated user
 */
async function fetchCurrentUser(): Promise<User> {
  const result = await getCurrentUser(apiService);

  if (result.success && result.data) {
    return normalizeCurrentUser(result.data as CurrentUserApiData);
  }

  throw new Error(result.error?.message || 'Failed to fetch current user data');
}

/**
 * Hook to fetch current user data
 */
export function useCurrentUserQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.users.current,
    queryFn: fetchCurrentUser,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Helper to get display name from user object
 */
export function getUserDisplayName(user: User | null | undefined): string {
  if (!user) return 'Unknown User';
  return user.displayName || user.name || user.email.split('@')[0];
}
